import { Link } from 'react-router-dom';
import {
  EDUCATION_REGISTRATION_STATUS_LABEL,
  type EducationRegistrationStatus,
} from '@platform/billing';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { useMyEducationRegistrations } from '../api/use-education.query';
import { formatDate, formatEducationPrice } from '../lib/education-format';

const STATUS_CLASS: Record<EducationRegistrationStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-700',
  CONFIRMED: 'bg-green-100 text-green-700',
  CANCELED: 'bg-red-100 text-red-600',
  EXPIRED: 'bg-gray-100 text-gray-500',
};

/** 내 교육 접수 내역. */
export function MyEducationListView() {
  const { data, isLoading, isError } = useMyEducationRegistrations();
  const registrations = data?.items ?? [];

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900 dark:text-neutral-100">
          내 교육 접수
        </h1>
        <Link
          to={PATHS.EDUCATION}
          className="text-sm text-gray-500 hover:text-gray-800 dark:text-neutral-400"
        >
          교육 둘러보기 →
        </Link>
      </div>

      {isLoading ? (
        <p className="py-16 text-center text-sm text-gray-500">불러오는 중…</p>
      ) : isError ? (
        <p className="py-16 text-center text-sm text-red-600">
          접수 내역을 불러오지 못했습니다.
        </p>
      ) : registrations.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 py-16 text-center dark:border-white/15">
          <p className="text-sm text-gray-500 dark:text-neutral-400">
            접수한 교육이 없습니다.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 dark:divide-white/10 dark:border-white/15">
          {registrations.map((r) => {
            const headCount = r.items.reduce((sum, i) => sum + i.quantity, 0);
            return (
              <li key={r.id}>
                <Link
                  to={PATHS.educationRegistration(r.id)}
                  className="flex items-center justify-between gap-3 px-4 py-3.5 transition-colors hover:bg-gray-50 dark:hover:bg-white/10"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[r.status]}`}
                      >
                        {EDUCATION_REGISTRATION_STATUS_LABEL[r.status]}
                      </span>
                      <span className="truncate font-medium text-gray-900 dark:text-neutral-100">
                        {r.items[0]?.sessionTitle ?? '교육 접수'}
                        {r.items.length > 1 ? ` 외 ${r.items.length - 1}건` : ''}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-400 dark:text-neutral-500">
                      {r.registrationNo} · 수강자 {headCount}명 ·{' '}
                      {formatDate(r.createdAt)}
                    </p>
                  </div>
                  <p className="shrink-0 font-semibold text-gray-900 dark:text-neutral-100">
                    {formatEducationPrice(r.totalAmount)}
                  </p>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
